/// <reference path="../../../../../node_modules/bingmaps/types/MicrosoftMaps/Microsoft.Maps.All.d.ts" />

/* System defined core library */
import { Component, Input, Output, ViewChild, ElementRef, SimpleChanges, OnDestroy, OnChanges } from '@angular/core';
import type { IMapMarker, IMapRegion } from '../../models/map.model';
import { MapUtilityService } from '../../services/map-utility.service';
import { BingMapsService } from '../../services/bing-map.service';

@Component({
    selector: 'app-bing-map',
    templateUrl: './bing-map.component.html',
    styleUrls: ['./bing-map.component.scss']
})
export class BingMapComponent implements OnChanges, OnDestroy {

    @ViewChild('bingMap') bingMapViewChild: ElementRef;

    @Input() latitude: number;
    @Input() longitude: number;
    @Input() zoom: number;
    @Input() markers: IMapMarker[];
    @Input() region: IMapRegion;

    bingMap: Microsoft.Maps.Map;
    isMapLoaded: boolean = false;
    mapOptions: Microsoft.Maps.IMapLoadOptions;
    ariaInterval: any;

    constructor(
        private bingMapsService: BingMapsService,
        private mapUtilityService: MapUtilityService) {

        // default map options
        this.mapOptions = {
            mapTypeId: Microsoft.Maps.MapTypeId.road,
            showLocateMeButton: false
        };
    }

    /**
     * Create map after view has been initialized and script has been downloaded
     * */
    ngAfterViewInit() {
        this.createMap();
    }

    /**
     * Changes to 'markers' proprety will trigger a redraw after waiting for .js to load
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {
        // marker changes take precedence in redrawing the map
        if (changes['markers']) {
            // only redraw after first change (which happens on init)
            if (!changes['markers'].firstChange) {
                this.bingMapsService.load().then(() => {
                    this.redrawMap();
                });
            }
        } else if (changes['latitude'] || changes['longitude']) {
            this.focusMap();
        }
    }

    /**
     * Dispose map and interval
     * */
    ngOnDestroy() {
        this.bingMap.dispose();
        if (this.ariaInterval) {
            clearInterval(this.ariaInterval);
        }
    }

    /**
     * Focus map on current lat, long point
     * */
    focusMap() {
        this.bingMap.setView({
            center: new Microsoft.Maps.Location(this.latitude, this.longitude),
            zoom: this.zoom
        });
    }

    /**
     * Redraw Bing map, uusally triggered after 'markers' changed
     * */
    redrawMap() {
        let pins: Microsoft.Maps.Pushpin[] = [];

        // add all data pins
        this.markers.forEach((m, i) => {
            pins.push(this.createFontPushpin(new Microsoft.Maps.Location(m.latitude, m.longitude), (i + 1).toString()));
        });

        // add region pin
        if (this.region) {
            pins.push(this.createFontPushpin(new Microsoft.Maps.Location(this.region.lat, this.region.long), '', 'purple'));
        }

        // set pins and view
        this.bingMap.entities.clear();
        this.bingMap.entities.push(pins);
        this.bingMap.setView({
            bounds: Microsoft.Maps.LocationRect.fromShapes(pins),
            zoom: this.zoom
        });

        // add aria-label to canvas
        this.updateAria();
    }

    /**
     * Update ARIA label for 'mapFocus' id once the element loads on the page, set an interval to ensure aria support
     * The interval should only be run once per map-load, and 'mapFocus' will exist after
     */
    updateAria() {
        // already running -- don't run again
        if (this.ariaInterval) {
            return;
        }

        let intervalCount = 0;
        let maxIntervals = 20;
        let intervalTime = 200;

        this.ariaInterval = setInterval(() => {
            if (intervalCount >= maxIntervals) {
                clearInterval(this.ariaInterval);
                this.ariaInterval = null;
            }
            let mapFocus = document.getElementById('mapFocus');
            if (mapFocus) {
                mapFocus.setAttribute('aria-label', 'Bing Maps, ' + this.markers.map((m, i) => (i + 1).toString()).join(' '));
                clearInterval(this.ariaInterval);
                this.ariaInterval = null;
            }
            intervalCount++;
        }, intervalTime);
    }

    /**
     * Create a new Bing map and attempt to update map if there is any marker data loaded into it after .js loads
     * */
    createMap() {
        this.bingMap = new Microsoft.Maps.Map(this.bingMapViewChild.nativeElement, this.mapOptions);
        this.bingMapsService.load().then(() => {
            this.redrawMap();
        });
    }

    /**
     * Create map pushpin using icons
     * @param location
     * @param text
     * @param color
     */
    createPushpin(location: Microsoft.Maps.Location, text: string, color: string = 'red') {
        return new Microsoft.Maps.Pushpin(location, {
            color: color,
            title: text,
            cursor: 'cursor',
            icon: '/Assets/images/country-marker-count.png'
        });
    }

    /**
     * Create map pushpin using font-awesome and title properties
     * @param location
     * @param text
     * @param color
     * @param fontText
     * @param fontName
     * @param fontSizePx
     */
    createFontPushpin(location: Microsoft.Maps.Location, text: string, color: string = 'red', fontText: string = '\uf041', fontName: string = 'FontAwesome', fontSizePx: number = 30) {
        var c = document.createElement('canvas');
        var ctx = c.getContext('2d');

        //Define font style
        var font = fontSizePx + 'px ' + fontName;
        ctx.font = font

        //Resize canvas based on size of text.
        var size = ctx.measureText(fontText);
        c.width = size.width;
        c.height = fontSizePx;

        //Reset font as it will be cleared by the resize.
        ctx.font = font;
        ctx.textBaseline = 'top';
        ctx.fillStyle = color;

        ctx.fillText(fontText, 0, 0);

        return new Microsoft.Maps.Pushpin(location, {
            icon: c.toDataURL(),
            title: text,
            color: 'black',
            cursor: 'pointer',
            anchor: new Microsoft.Maps.Point(c.width / 2, c.height / 2) //Align center of pushpin with location.
        });
    }
}
